import type {
  CollectionRecord,
  CollectionResourceRef,
  CollectionResourceType,
} from "@/shared/domain/collections";

/**
 * Roboto ID prefix for each type a collection can hold.
 *
 * Every member type is prefixed, so a member's type is always recoverable from
 * its ID alone — which is why the collection tools take bare ID arrays rather
 * than `{resource_type, resource_id}` pairs. A pair lets the model emit an
 * internally contradictory value (`{resource_type: "file", resource_id:
 * "ev_a"}`); a bare ID cannot be wrong about itself.
 *
 * Keyed by the union, not by `string`: a member type with no prefix would make
 * {@link resourceTypeFromId} return undefined for every valid ID of that type,
 * and {@link findTypeMismatches} would then reject all of them. As a `Record`
 * over the union that is a compile error instead.
 */
export const RESOURCE_TYPE_PREFIXES: Record<CollectionResourceType, string> = {
  dataset: "ds",
  event: "ev",
  file: "fl",
  session: "se",
};

/** {@link RESOURCE_TYPE_PREFIXES} inverted, for reading a type off an ID. */
const PREFIX_TO_RESOURCE_TYPE: Readonly<
  Record<string, CollectionResourceType>
> = Object.fromEntries(
  Object.entries(RESOURCE_TYPE_PREFIXES).map(([resourceType, prefix]) => [
    prefix,
    resourceType,
  ]),
) as Record<string, CollectionResourceType>;

/**
 * The collection-member type an ID denotes, or undefined if it denotes none.
 *
 * Matches the whole token before the first underscore rather than using
 * `startsWith`, since Roboto prefixes shadow each other as string prefixes.
 */
export function resourceTypeFromId(
  id: string,
): CollectionResourceType | undefined {
  const separator = id.indexOf("_");
  if (separator <= 0 || separator === id.length - 1) {
    return undefined;
  }
  return PREFIX_TO_RESOURCE_TYPE[id.slice(0, separator)];
}

/**
 * IDs from `ids` that are not members of type `expected`.
 *
 * Collections are homogeneous — the backend rejects a mismatched add with
 * "Cannot add resource of type X to a Y collection" — so catching this locally
 * turns a server round trip into an error the model can act on immediately.
 */
export function findTypeMismatches(
  ids: readonly string[],
  expected: CollectionResourceType,
): string[] {
  return ids.filter((id) => resourceTypeFromId(id) !== expected);
}

/**
 * Display noun for each collection member type, singular.
 *
 * One map, shared by both collection confirmation components: they used to
 * declare identically-named constants with different semantics (a display map
 * in one, an identity map in the other), which is how "Add 3 dataset" and
 * "Add 3 datasets" end up one edit apart.
 *
 * The nouns currently match their keys, but the map earns its place twice
 * over: it is where an irregular noun would go, and `RESOURCE_TYPES` derives
 * the AI tools' `resource_type` enum from its keys, so being a `Record` over
 * the union is what makes a new member type a compile error there.
 */
export const RESOURCE_TYPE_NOUNS: Record<CollectionResourceType, string> = {
  dataset: "dataset",
  event: "event",
  file: "file",
  session: "session",
};

/**
 * Neutral noun for a count whose type cannot be stated, e.g. because the
 * proposed IDs contradict the collection they are being added to.
 */
export const NEUTRAL_RESOURCE_NOUN = "item";

/**
 * "datasets" — every noun this module pluralizes is regular, so the plural is
 * derived rather than stored. An irregular member type would need this to
 * consult {@link RESOURCE_TYPE_NOUNS} instead.
 */
export function pluralize(noun: string): string {
  return `${noun}s`;
}

/** "1 dataset" / "3 datasets" — pluralizes by count. */
export function countLabel(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : pluralize(noun)}`;
}

/**
 * Join nouns into an English list: "a, b, or c".
 *
 * Every sentence in the collection tools that enumerates the member types goes
 * through here, so a new type reaches all of them at once. Hand-written copies
 * are what left the tool schemas telling the model that only datasets, files,
 * and events exist while their own enum already offered sessions.
 */
function joinNouns(nouns: readonly string[]): string {
  if (nouns.length <= 1) {
    return nouns[0] ?? "";
  }
  return `${nouns.slice(0, -1).join(", ")}, or ${nouns[nouns.length - 1]}`;
}

/** "dataset, event, file, or session" — the member types, singular. */
export function resourceNounList(): string {
  return joinNouns(Object.values(RESOURCE_TYPE_NOUNS));
}

/** "datasets, events, files, or sessions" — the member types, plural. */
export function resourceNounListPlural(): string {
  return joinNouns(Object.values(RESOURCE_TYPE_NOUNS).map(pluralize));
}

/**
 * "dataset IDs ('ds_'), event IDs ('ev_'), …" — what each member type's IDs
 * look like, for a tool schema that must tell the model which prefix to use.
 */
export function resourceIdPrefixList(): string {
  return joinNouns(
    (Object.keys(RESOURCE_TYPE_NOUNS) as CollectionResourceType[]).map(
      (type) =>
        `${RESOURCE_TYPE_NOUNS[type]} IDs ('${RESOURCE_TYPE_PREFIXES[type]}_')`,
    ),
  );
}

/**
 * Describe an ID's type for an error message, naming unrecognized IDs plainly.
 */
export function describeId(id: string): string {
  const resourceType = resourceTypeFromId(id);
  return resourceType
    ? `${id} is a ${resourceType}`
    : `${id} is not a recognized ${resourceNounList()} ID`;
}

/**
 * Build the wire-shape resource refs the collections API expects.
 *
 * The type is stamped from `resourceType` rather than re-derived per ID: the
 * caller has already established that every ID belongs to this collection
 * (see {@link findTypeMismatches}), and re-deriving here would silently emit a
 * ref of a different type for an ID that slipped through.
 */
export function toResourceRefs(
  ids: readonly string[],
  resourceType: CollectionResourceType,
): CollectionResourceRef[] {
  return ids.map((id) => ({ resource_id: id, resource_type: resourceType }));
}

/**
 * The ID an entry of a collection's `resources` / `missing` map denotes.
 *
 * The API returns the two maps in different shapes — `missing` holds bare
 * `{resource_id, resource_type}` refs, while `resources` comes back hydrated
 * into full records whose ID field is named for its type (`dataset_id`,
 * `file_id`, `event_id`, `session_id`). That naming is why the lookup below is
 * built from `resourceType` rather than a fixed list -- it needs no edit when a
 * member type is added. Read both, plus a bare string, so a change of content
 * mode degrades to a smaller count rather than a wrong one.
 */
function entryId(entry: unknown, resourceType: string): string | undefined {
  if (typeof entry === "string") {
    return entry;
  }
  if (typeof entry !== "object" || entry === null) {
    return undefined;
  }
  const fields = entry as Record<string, unknown>;
  const candidate = fields.resource_id ?? fields[`${resourceType}_id`];
  return typeof candidate === "string" ? candidate : undefined;
}

function idsFromMap(map: { [key: string]: unknown[] } | undefined): string[] {
  if (!map) {
    return [];
  }
  return Object.entries(map).flatMap(([resourceType, entries]) =>
    (entries ?? [])
      .map((entry) => entryId(entry, resourceType))
      .filter((id): id is string => id !== undefined),
  );
}

/**
 * IDs of the members a collection record actually holds.
 *
 * This is the answer to "what happened", as opposed to what was asked for:
 * an add is an upsert and a remove is a `DELETE ... WHERE` that matches
 * nothing for a non-member, so the request is not evidence of the outcome.
 */
export function collectionMemberIds(record: CollectionRecord): string[] {
  return idsFromMap(record.resources);
}

/**
 * IDs the collection references but could not resolve.
 *
 * The server routes unresolvable and cross-org references here while still
 * creating (or updating) the collection, so this is the only signal that a
 * write succeeded only in part.
 */
export function collectionMissingIds(record: CollectionRecord): string[] {
  return idsFromMap(record.missing);
}
